import { ApiQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { ModuleRendering, SlotRendering, applyModuleRenderableFlags, isModuleRenderingRenderable } from '@frontend/common-types';
import { Logger } from '@frontend/logger';
import { ModuleClient } from '@frontend/module/api';
import { Module } from '@frontend/module/types';
import { useAccountRepository, useListRepository } from '@frontend/repository';
import { Spot } from '@frontend/spot/types';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useAppDispatch } from '../../../redux/store';
import { calculateBoundingBox, calculateScale } from '../../utils';

interface UseSpotLayoutControllerProps<TModule extends ModuleRendering = ModuleRendering, TSlot extends SlotRendering = SlotRendering> {
    spot?: Spot;
    module?: Module;
    renderSingleModule?: boolean;
    fetchSpotData: (arg?: ApiQueryParams<string | number>) => Promise<PaginatedResponse<TModule>>;
    fetchModuleData: (accountId: string, spotId: string, moduleId: string) => Promise<{ results: TSlot[] }>;
}

export const useSpotLayoutController = <TModule extends ModuleRendering = ModuleRendering, TSlot extends SlotRendering = SlotRendering>(
    props: UseSpotLayoutControllerProps<TModule, TSlot>
) => {
    const { currentAccount } = useAccountRepository();
    const dispatch = useAppDispatch();
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
    const [selectedModuleSlots, setSelectedModuleSlots] = useState<TSlot[] | null>(null);
    const [moduleRenderableById, setModuleRenderableById] = useState<ReadonlyMap<string, boolean | undefined>>(new Map());

    const repository = useListRepository<TModule>({
        fetch: (arg) => props.fetchSpotData(arg),
        dispatch,
        listId: `spot-layout-${props.spot ? props.spot.id : props.module ? props.module.id : 'default'}`
    });

    const data = repository.data;
    const hasLayoutData = !!data?.length;

    const spotIdForFlags = props.spot?.id ?? props.module?.spot_id ?? data?.[0]?.spot_id;

    // Spot rendering may omit/default `renderable`; prefer the module-api flag.
    useEffect(() => {
        if (!currentAccount || !spotIdForFlags) {
            return;
        }
        let cancelled = false;
        ModuleClient.fetchModules({
            account_id: currentAccount,
            spot_id: spotIdForFlags,
            size: '1000'
        })
            .then((res) => {
                if (cancelled) return;
                setModuleRenderableById(new Map(res.results.map((module) => [module.id, module.renderable])));
            })
            .catch((error) => {
                Logger.error('Error fetching module renderable flags:', error);
            });
        return () => {
            cancelled = true;
        };
    }, [currentAccount, spotIdForFlags]);

    // Track the actual layout viewport, including size changes caused by surrounding UI.
    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const updateDimensions = (width: number, height: number) => {
            setDimensions((current) => (current.width === width && current.height === height ? current : { width, height }));
        };

        const measureContainer = () => {
            const styles = window.getComputedStyle(container);
            const horizontalPadding = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
            const verticalPadding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
            updateDimensions(Math.max(container.clientWidth - horizontalPadding, 0), Math.max(container.clientHeight - verticalPadding, 0));
        };

        measureContainer();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measureContainer);
            return () => window.removeEventListener('resize', measureContainer);
        }

        const resizeObserver = new ResizeObserver(([entry]) => {
            if (entry) updateDimensions(entry.contentRect.width, entry.contentRect.height);
        });
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, [hasLayoutData]);

    // Auto-select first module when renderSingleModule is true
    useEffect(() => {
        if (props.renderSingleModule && data && data.length > 0 && !selectedModuleId) {
            const firstModule = data[0];
            handleModuleExpand(firstModule.id, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.renderSingleModule, data]);

    const handleModuleExpand = async (moduleId: string, forceExpand = false) => {
        if (!props.spot) return;

        if (selectedModuleId === moduleId && !forceExpand) {
            // Collapse - go back to all modules (only if not forced and not in single module mode)
            if (!props.renderSingleModule) {
                setSelectedModuleId(null);
                setSelectedModuleSlots(null);
            }
        } else {
            // Expand - fetch and show only this module
            try {
                const res = await props.fetchModuleData(currentAccount, props.spot.id, moduleId);
                setSelectedModuleSlots(res.results);
                setSelectedModuleId(moduleId);
            } catch (error) {
                Logger.error('Error fetching module slots:', error);
            }
        }
    };

    const handleNavigateModule = (direction: 'prev' | 'next') => {
        if (!data || data.length === 0) return;

        const currentIndex = data.findIndex((m) => m.id === selectedModuleId);
        if (currentIndex === -1) return;

        let newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;

        // Wrap around
        if (newIndex < 0) newIndex = data.length - 1;
        if (newIndex >= data.length) newIndex = 0;

        handleModuleExpand(data[newIndex].id, true);
    };

    const modules: TModule[] = useMemo(() => applyModuleRenderableFlags(data || [], moduleRenderableById) as TModule[], [data, moduleRenderableById]);

    const nonRenderableModuleCount = modules.filter((module) => !isModuleRenderingRenderable(module)).length;

    // Geometric layout only — virtual modules often sit at 0,0,0 and skew the bounding box.
    const modulesToRender = (selectedModuleId ? modules.filter((m) => m.id === selectedModuleId) : modules).filter(isModuleRenderingRenderable);

    // If a module is selected, use the fetched slots instead of the module's default slots
    const modulesWithSlots = modulesToRender.map((module) => {
        if (module.id === selectedModuleId && selectedModuleSlots) {
            return { ...module, slots: selectedModuleSlots as TModule['slots'] };
        }
        return module;
    });

    const bbox = calculateBoundingBox<TModule>(modulesWithSlots);
    const padding = 40;
    const topPadding = 50;
    const scale = calculateScale(bbox.width, bbox.height, dimensions.width, dimensions.height, padding * 2, padding + topPadding);

    const containerWidth = bbox.width * scale + padding * 2;
    const containerHeight = bbox.height * scale + padding + topPadding;

    const centerOffsetX = selectedModuleId ? (containerWidth - bbox.width * scale) / 2 - bbox.minX * scale : padding + -bbox.minX * scale;
    const centerOffsetY = selectedModuleId ? (containerHeight - bbox.height * scale) / 2 + bbox.maxY * scale : topPadding + bbox.maxY * scale;

    const currentModule = selectedModuleId ? modules.find((m) => m.id === selectedModuleId) : null;
    const isContainerMeasured = dimensions.width > 0 && dimensions.height > 0;

    return {
        containerRef,
        isContainerMeasured,
        data,
        selectedModuleId,
        modules,
        modulesWithSlots,
        nonRenderableModuleCount,
        currentModule,
        dimensions,
        scale,
        centerOffsetX,
        centerOffsetY,
        containerWidth,
        containerHeight,
        handleModuleExpand,
        handleNavigateModule
    };
};
